/**
 * RecordingPipeline — owns mic permission, audio capture, and the lifecycle
 * of the recording buffer during a decision (Arch §2.4, PRD §10.1).
 *
 * V1 §A1 scope: capture audio via MediaRecorder + getUserMedia, hold the
 * resulting Blob in memory, and discard it on stop. Whisper transcription
 * (§A2) will plug into stop() in a follow-up slice. The interface returns
 * a Blob today; when WhisperAdapter lands the buffer will flow straight
 * into transcribe().
 *
 * Permission model:
 *   - `requestPermission()` triggers the OS prompt by calling getUserMedia
 *     and immediately stopping the tracks. Returns the standard
 *     PermissionState ('granted' | 'denied' | 'prompt'). 'prompt' should
 *     not normally appear (we explicitly asked) but we return it if
 *     getUserMedia for some reason resolves without granting.
 *   - Once granted at the browser level, subsequent start() calls do not
 *     re-trigger the OS prompt within the same browser-permission session.
 *
 * Single shared instance, exported as `recordingPipeline`.
 */

export interface RecordingPipeline {
  /**
   * Trigger the OS mic permission prompt. Resolves to 'granted' if the
   * user accepted, 'denied' otherwise. Safe to call multiple times — the
   * browser only prompts when state is 'prompt'.
   */
  requestPermission(): Promise<PermissionState>;

  /** Begin capturing audio. Resolves once the recorder is running. */
  start(): Promise<void>;

  /**
   * Stop capturing and return the captured audio as a Blob. If no recording
   * is in progress, resolves with an empty blob.
   */
  stop(): Promise<Blob>;

  /** Stop and discard without returning the buffer. */
  cancel(): void;

  /** Whether a recording is currently active. */
  isRecording(): boolean;
}

class V1RecordingPipeline implements RecordingPipeline {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private active = false;
  /**
   * In-flight start() resolution. Captured so that:
   *   1. Concurrent start() calls share the same async work
   *      (no duplicate getUserMedia prompts in fast re-renders)
   *   2. stop() awaits start before deciding the recorder isn't active
   *      — fixes the race where the user confirms an action before
   *      MediaRecorder finishes setting up, which previously returned
   *      an empty Blob and dropped the transcript.
   */
  private startPromise: Promise<void> | null = null;

  async requestPermission(): Promise<PermissionState> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      return 'denied';
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately release — we'll request the stream again on start().
      // (Holding the mic open between prompts and first hand would show a
      // persistent "recording" indicator in some browsers / OSes.)
      stream.getTracks().forEach((t) => t.stop());
      return 'granted';
    } catch {
      return 'denied';
    }
  }

  start(): Promise<void> {
    if (this.active) return Promise.resolve();
    if (this.startPromise) return this.startPromise;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      return Promise.reject(
        new Error('RecordingPipeline.start: mediaDevices unavailable'),
      );
    }
    this.startPromise = (async () => {
      try {
        this.chunks = [];
        // Track constraints tuned for speech-to-text quality:
        //   echoCancellation / noiseSuppression / autoGainControl — let
        //     the browser pre-process the signal. Materially better on
        //     average laptop mics in noisy rooms.
        //   channelCount: 1 — Whisper consumes mono anyway, save the bits.
        //   sampleRate: 16000 — Whisper's native rate. Browsers may not
        //     honor it (some clamp to 48000), but it's a free hint.
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 16_000,
          },
        });
        // Prefer opus when available (modern browsers); MediaRecorder
        // gracefully falls back to whatever the implementation supports.
        const preferredMime = 'audio/webm;codecs=opus';
        const mimeType =
          typeof MediaRecorder !== 'undefined' &&
          MediaRecorder.isTypeSupported(preferredMime)
            ? preferredMime
            : undefined;
        this.recorder = new MediaRecorder(this.stream, {
          ...(mimeType ? { mimeType } : {}),
          audioBitsPerSecond: 128_000,
        });
        this.recorder.ondataavailable = (e) => {
          if (e.data.size > 0) this.chunks.push(e.data);
        };
        this.recorder.start();
        this.active = true;
      } finally {
        this.startPromise = null;
      }
    })();
    return this.startPromise;
  }

  async stop(): Promise<Blob> {
    // Critical: if start() is still in flight, wait for it before deciding
    // whether the recorder is active. Without this, a fast click after
    // hero's turn begins would land here with active === false and return
    // an empty Blob.
    if (this.startPromise) {
      try {
        await this.startPromise;
      } catch {
        // Start failed → no recording to stop. Fall through to the
        // not-active path below.
      }
    }
    if (!this.active || !this.recorder) {
      this.cleanup();
      return new Blob([]);
    }
    return new Promise((resolve) => {
      const recorder = this.recorder!;
      recorder.onstop = () => {
        const blob = new Blob(this.chunks, {
          type: recorder.mimeType || 'audio/webm',
        });
        this.cleanup();
        resolve(blob);
      };
      try {
        recorder.stop();
      } catch {
        // Already stopped — resolve with whatever we have.
        const blob = new Blob(this.chunks, {
          type: recorder.mimeType || 'audio/webm',
        });
        this.cleanup();
        resolve(blob);
      }
    });
  }

  cancel(): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.stop();
      } catch {
        /* ignore */
      }
    }
    this.cleanup();
  }

  isRecording(): boolean {
    return this.active;
  }

  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.recorder = null;
    this.chunks = [];
    this.active = false;
  }
}

/** Single shared instance used across the app. */
export const recordingPipeline: RecordingPipeline = new V1RecordingPipeline();
